import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    try {
      const errorData = await res.json();
      const message = errorData.message || `Request failed with status ${res.status}`;

      // Create error object with additional response data for 2FA handling
      const error = new Error(message) as any;
      error.status = res.status;
      error.response = { status: res.status, data: errorData };

      throw error;
    } catch (jsonError) {
      // If JSON parsing fails, fall back to text
      const text = res.statusText || `Request failed with status ${res.status}`;
      const error = new Error(text) as any;
      error.status = res.status;
      throw error;
    }
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};

  if (supabase) {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      console.error('[AUTH] Failed to get session:', error);
    } else if (session?.access_token) {
      // Only log in development mode to reduce console noise
      if (import.meta.env.DEV) {
        console.log('[AUTH] Adding Bearer token to request');
      }
      headers.Authorization = `Bearer ${session.access_token}`;
    } else {
      console.log('[AUTH] No session or access token available');
    }
  } else {
    console.log('[AUTH] Supabase client not available');
  }

  return headers;
}

// Helper to get auth token, needed for the new apiRequest signature
async function getAuthToken(): Promise<string | null> {
  if (supabase) {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      console.error('[AUTH] Failed to get session:', error);
    } else if (session?.access_token) {
      return session.access_token;
    }
  }

  // Fallback: attempt to read stored Supabase session directly from localStorage
  if (typeof window !== "undefined" && "localStorage" in window) {
    try {
      const stored = window.localStorage.getItem("sb-auth-token");
      if (stored) {
        const parsed = JSON.parse(stored);
        const token =
          parsed?.currentSession?.access_token ||
          parsed?.access_token ||
          parsed?.token ||
          null;
        if (token) {
          console.log("[AUTH] Using access token from localStorage fallback");
          return token;
        }
      }
    } catch (error) {
      console.warn("[AUTH] Failed to parse sb-auth-token from localStorage:", error);
    }
  }
  return null;
}

type ApiRequestOptions = {
  headers?: HeadersInit;
  body?: any;
  timeout?: number;
};

function normalizeApiRequestOptions(
  options?: ApiRequestOptions | BodyInit | Record<string, unknown> | null
): ApiRequestOptions {
  if (options === null || options === undefined) {
    return {};
  }

  const hasKnownOptionKeys =
    typeof options === "object" &&
    options !== null &&
    ("headers" in options || "body" in options || "timeout" in options);

  // Backwards compatibility: treat plain bodies (objects, strings, FormData, etc.)
  // as the request body when the caller doesn't pass the new options shape.
  if (!hasKnownOptionKeys) {
    return { body: options };
  }

  return options as ApiRequestOptions;
}

export async function apiRequest(
  method: string,
  url: string,
  options: ApiRequestOptions | BodyInit | Record<string, unknown> | null = {}
) {
  const normalizedMethod = method.toUpperCase();
  const allowsRequestBody = normalizedMethod !== "GET" && normalizedMethod !== "HEAD";

  const { headers = {}, body, timeout = 30000 } = normalizeApiRequestOptions(options); // 30 second timeout

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  const defaultHeaders: HeadersInit = {};

  if (!isFormData) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  // Add auth token if available
  const token = await getAuthToken();
  if (token) {
    (defaultHeaders as any)['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    method: normalizedMethod,
    headers: {
      ...defaultHeaders,
      ...headers,
    },
  };

  if (body !== undefined && body !== null && allowsRequestBody) {
    if (isFormData) {
      config.body = body;
    } else if (typeof body === 'string') {
      config.body = body;
    } else {
      config.body = JSON.stringify(body);
    }
  } else if (body !== undefined && body !== null && !allowsRequestBody) {
    console.warn(`[API REQUEST] Ignoring request body for ${normalizedMethod} ${url}`);
  }

  // Always include credentials so cookie-based sessions remain valid
  config.credentials = "include";

  // Add timeout wrapper
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  config.signal = controller.signal;

  try {
    console.log(`[API REQUEST] ${normalizedMethod} ${url}`, body && allowsRequestBody ? { body } : '');

    const response = await fetch(url, config);
    clearTimeout(timeoutId);

    console.log(`[API REQUEST] ${normalizedMethod} ${url} - ${response.status}`);

    if (!response.ok) {
      let errorMessage;
      try {
        const errorBody = await response.json();
        errorMessage = errorBody.error || errorBody.message || `HTTP ${response.status}`;
        console.error(`[API REQUEST] Error response:`, errorBody);
      } catch {
        errorMessage = `HTTP ${response.status} ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      console.error(`[API REQUEST] Request timeout: ${normalizedMethod} ${url}`);
      throw new Error('Request timeout - please try again');
    }
    console.error(`[API REQUEST] Request failed: ${normalizedMethod} ${url}`, error);
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const authHeaders = await getAuthHeaders();

    // Handle query keys with parameters properly
    let url: string;
    if (Array.isArray(queryKey) && queryKey.length > 1) {
      const [baseUrl, params] = queryKey;
      if (typeof params === 'object' && params !== null) {
        // Convert object parameters to query string
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            searchParams.append(key, String(value));
          }
        });
        url = `${baseUrl}?${searchParams.toString()}`;
      } else {
        url = queryKey.join("/");
      }
    } else {
      url = Array.isArray(queryKey) ? queryKey[0] : String(queryKey);
    }

    const res = await fetch(url, {
      headers: authHeaders,
      // Use 'same-origin' instead of 'include' to avoid CORS issues
      // JWT tokens in Authorization header handle authentication
      credentials: "same-origin",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      staleTime: 5 * 60 * 1000, // 5 minutes instead of Infinity for better UX
      gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
      retry: false,
      networkMode: 'always',
    },
    mutations: {
      retry: false,
      networkMode: 'always',
    },
  },
});
